import { el, fragment } from '../dom.js';
import { admin, adminRaw } from '../admin-api.js';
import { t, formatMoment } from '../i18n.js';

// The list is the deliverable: today the only way to know which venues are
// covered is to query the database.
//
// Read-only with one exception — the emergency switch. The registry file in
// jobs/crawl/sources/ stays the truth, because the `verified:` lines are worth
// having precisely as a reviewable diff. What the dashboard gets is a veto: a
// venue relaunches, the extractor starts producing rubbish, and the fix is one
// commit away, which is one commit too far when the queue fills tonight.
export async function sourcesPanel({ reload }) {
  const data = await admin('GET', '/crawl/sources');
  const sources = data?.sources ?? [];

  return fragment([
    el('p', { class: 'card-meta' }, t.crawlSources(sources.length)),
    el('ul', { class: 'card-list' }, sources.map((source) => el('li', {}, card(source, reload)))),
  ]);
}

const HEALTH = {
  ok: () => ({ label: t.crawlHealthOk, badge: 'badge-live' }),
  stalled: () => ({ label: t.crawlHealthStalled, badge: 'badge-draft' }),
  never: () => ({ label: t.crawlHealthNever, badge: 'badge-draft' }),
  disabled: () => ({ label: t.crawlHealthDisabled, badge: 'badge-draft' }),
};

function card(source, reload) {
  const health = (HEALTH[source.health] ?? HEALTH.never)();

  return el('div', { class: 'card' }, [
    el('div', { class: 'row' }, [
      el('h4', { class: 'card-title' }, source.label),
      el('span', { class: `badge ${health.badge} row-end` }, health.label),
    ]),
    el('p', { class: 'card-meta' }, [
      source.key,
      source.kind ? ` · ${source.kind}` : '',
      source.host ? ` · ${source.host}` : '',
    ].join('')),
    el('p', { class: 'card-meta' }, [
      t.crawlYield(source.yieldLast, source.yieldTotal),
      source.lastSuccessAt ? ` · ${formatMoment(source.lastSuccessAt)}` : '',
      source.pending ? ` · ${t.crawlPending(source.pending)}` : '',
    ].join('')),
    // A mark and never a colour: a source can be delivering perfectly and still
    // be one nobody recorded a robots or TDM state for, and folding that into
    // the health dot lets a green source hide it.
    //
    // The mark only — the sentence behind it lives once, on the overview. It is
    // true of every source today, so repeating the explanation fifty-four times
    // is how a real precondition turns into wallpaper.
    source.unchecked ? el('p', { class: 'card-meta' }, t.crawlUnchecked) : '',
    source.lastError ? el('p', { class: 'card-meta' }, String(source.lastError).slice(0, 160)) : '',
    source.disabledReason
      ? el('p', { class: 'card-meta' }, `${t.crawlHealthDisabled}: ${source.disabledReason}`)
      : '',
    switcher(source, reload),
  ].filter(Boolean));
}

function switcher(source, reload) {
  // A source the registry itself disabled has nothing to veto — offering a
  // switch there would suggest the dashboard can turn it on, which it cannot.
  if (!source.enabled) return '';

  if (source.disabledAt) {
    const on = el('button', { type: 'button', class: 'btn' }, t.crawlEnable);
    on.addEventListener('click', async () => {
      on.disabled = true;
      await adminRaw('POST', `/crawl/sources/${encodeURIComponent(source.key)}/disabled`, { disabled: false });
      await reload();
    });
    return el('div', { class: 'row' }, on);
  }

  const reason = el('input', { type: 'text', class: 'input', placeholder: t.crawlDisableReason });
  const off = el('button', { type: 'button', class: 'btn' }, t.crawlDisable);
  off.addEventListener('click', async () => {
    off.disabled = true;
    await adminRaw('POST', `/crawl/sources/${encodeURIComponent(source.key)}/disabled`, {
      disabled: true, reason: reason.value.trim() || null,
    });
    await reload();
  });
  return el('div', { class: 'row' }, [reason, off]);
}
